import { useCallback, useEffect, useState } from "react";
import { DailyReportAlreadyExistsErrorData } from "@carry/contract";
import {
  DEFAULT_TIME_SLOT_BOUNDARIES,
  parseTimeOfDayMinutes,
  type TimeSlotBoundaries,
} from "@carry/shared";
import type {
  BackfillBlockedReasonDto,
  BackfillRequestDto,
  BackfillRequestStatusDto,
  DriverDailyReportDto,
  RouteDto,
  RouteStopDto,
  NotificationSettingsDto,
  UpdateNotificationSettingsInputDto,
  DriverAttendanceDto,
  DriverDeliverySafetyDto,
  ClockInInputDto,
  ClockOutInputDto,
  ReportLocationInputDto,
  DriverExpenseDto,
  CreateExpenseInputDto,
  UpdateExpenseInputDto,
  ExpenseStopOptionDto,
  DailyReportStatusDto,
  SubmitDailyReportInputDto,
  DailyReportRecoveryCandidateDto,
  ListDailyReportRecoveryCandidatesInputDto,
  RecoverDailyReportInputDto,
  DailyReportAlreadyExistsErrorDataDto,
  ResubmitDailyReportInputDto,
  VehicleInspectionDto as VehicleInspectionContractDto,
  CreateVehicleInspectionInputDto,
  InspectionItemResultDto,
  AlcoholCheckDto as AlcoholCheckContractDto,
  CreateAlcoholCheckInputDto,
  AlcoholCheckResultDto,
  DriverRefuelingDto as RefuelingContractDto,
  CreateRefuelingInputDto,
  UpdateRefuelingInputDto,
  VehicleDto,
} from "@carry/contract";
import type { StoredDriverSession } from "./auth";

/** /api/v1/driver/routes/today が返す Route stop / Route. 型の単一源泉は @carry/contract */
export type DriverRouteStop = RouteStopDto;
export type DriverRoute = RouteDto;

export interface UseDriverScheduleResult {
  routes: DriverRoute[] | null;
  timeSlotBoundaries: TimeSlotBoundaries;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export interface DriverFetcher {
  apiBaseUrl: string;
  session: StoredDriverSession | null;
  /** テスト時に差し替え可能な fetch 関数 (未指定なら global fetch) */
  fetchImpl?: typeof fetch;
}

function parseTimeSlotBoundaries(
  value:
    | {
        early_morning_until_time?: unknown;
        am_until_time?: unknown;
      }
    | null
    | undefined,
): TimeSlotBoundaries | null {
  if (
    typeof value?.early_morning_until_time !== "string" ||
    typeof value.am_until_time !== "string"
  ) {
    return null;
  }
  const earlyMinutes = parseTimeOfDayMinutes(value.early_morning_until_time);
  const amMinutes = parseTimeOfDayMinutes(value.am_until_time);
  if (
    earlyMinutes === null ||
    amMinutes === null ||
    earlyMinutes === 0 ||
    earlyMinutes >= amMinutes
  ) {
    return null;
  }
  return {
    earlyMorningUntil: value.early_morning_until_time,
    amUntil: value.am_until_time,
  };
}

/**
 * 認証は httpOnly Cookie (Better-Auth セッション) で行うため、全リクエストに
 * `credentials: 'include'` を付与して Cookie を送る。Authorization: Bearer は使わない。
 * driver.* ↔ api.* は same-site のため Cookie は first-party として送信される。
 */
function authedFetch(opts: DriverFetcher): typeof fetch {
  const base = opts.fetchImpl ?? fetch;
  return ((input: Parameters<typeof fetch>[0], init?: Parameters<typeof fetch>[1]) =>
    base(input, { ...init, credentials: "include" })) as typeof fetch;
}

/**
 * driver 本日ルート取得 hook.
 * 認証セッションが null の場合は routes=null を返して UI 側で mock/empty を出せるようにする.
 * polling interval はデフォルト 60 秒 (pollMs=0 で無効化).
 */
export function useDriverSchedule(
  opts: DriverFetcher & { pollMs?: number; deliveryDate?: string },
): UseDriverScheduleResult {
  const [routes, setRoutes] = useState<DriverRoute[] | null>(null);
  const [timeSlotBoundaries, setTimeSlotBoundaries] = useState<TimeSlotBoundaries>(
    DEFAULT_TIME_SLOT_BOUNDARIES,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!opts.session) {
      setRoutes(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fetchFn = authedFetch(opts);
      // deliveryDate 指定時 (「明日」タブ等) は ?delivery_date= で対象日を切り替える
      const qs = opts.deliveryDate ? `?delivery_date=${encodeURIComponent(opts.deliveryDate)}` : "";
      const res = await fetchFn(
        `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/routes/today${qs}`,
        {},
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
      }
      const payload = (await res.json()) as {
        data: DriverRoute[];
        time_slot_boundaries?: {
          early_morning_until_time?: unknown;
          am_until_time?: unknown;
        };
      };
      // 配列でない data をそのまま state に入れると routesToView が throw して画面が落ちる。
      // また空表示に倒すとドライバーが「予定なし」と誤認するため、取得失敗として扱う (Issue #471)。
      if (!Array.isArray(payload.data)) {
        throw new Error("配送予定の応答形式が不正です");
      }
      setRoutes(payload.data);
      setTimeSlotBoundaries(
        parseTimeSlotBoundaries(payload.time_slot_boundaries) ?? DEFAULT_TIME_SLOT_BOUNDARIES,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [opts.apiBaseUrl, opts.session, opts.fetchImpl, opts.deliveryDate]);

  useEffect(() => {
    void refetch();
    const pollMs = opts.pollMs ?? 60_000;
    if (pollMs <= 0) return;
    let id: ReturnType<typeof setInterval> | null = setInterval(() => void refetch(), pollMs);
    const onVisibility = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "hidden") {
        if (id) {
          clearInterval(id);
          id = null;
        }
      } else {
        // 復帰時は即座に同期 + interval 再開
        void refetch();
        if (!id) id = setInterval(() => void refetch(), pollMs);
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    return () => {
      if (id) clearInterval(id);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [refetch, opts.pollMs]);

  return { routes, timeSlotBoundaries, loading, error, refetch };
}

export interface UpdateStopStatusInput {
  status?: DriverRouteStop["status"];
  actual_arrival?: string | null;
  actual_departure?: string | null;
  estimated_arrival?: string | null;
  estimated_departure?: string | null;
  /** F10 監査連携: ステータス変更時のドライバー GPS 現在地 (best-effort)。 */
  latitude?: string;
  longitude?: string;
  /** F29 委託先支払距離: 配達先到着時に入力する ODO (km, 整数, 任意)。空欄なら未送信。 */
  arrival_odometer?: number | null;
  /** 取消などステータス更新理由。API 側の配送ステータス履歴に保存される。 */
  comment?: string;
}

export class DriverHttpError<TData = unknown> extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly data?: TData,
  ) {
    super(message);
    this.name = "DriverHttpError";
  }
}

async function toDriverHttpError(res: Response): Promise<DriverHttpError> {
  const body = (await res.json().catch(() => ({}))) as {
    message?: string;
    error?: string;
    data?: unknown;
  };
  return new DriverHttpError(
    body.message ?? body.error ?? `HTTP ${res.status}`,
    res.status,
    body.data,
  );
}

/** ネットワーク断・一時障害だけを再送し、認可拒否などの恒久エラーはキューへ残さない。 */
export function shouldQueueDriverError(error: unknown): boolean {
  if (!(error instanceof DriverHttpError)) return true;
  return error.status === 408 || error.status === 429 || error.status >= 500;
}

/**
 * stop ステータス更新. 成功時に更新後の stop を返す.
 * 呼び出し側は useDriverSchedule の refetch で UI を同期する.
 */
export async function updateDriverStopStatus(
  opts: DriverFetcher,
  stopPublicId: string,
  input: UpdateStopStatusInput,
): Promise<DriverRouteStop> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const res = await fetchFn(
    `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/stops/${stopPublicId}/status`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) {
    throw await toDriverHttpError(res);
  }
  const payload = (await res.json()) as { data: DriverRouteStop };
  return payload.data;
}

// ───── 勤怠 ─────

/** 勤怠 DTO. 型の単一源泉は @carry/contract (driver-attendance.ts)。 */
export type DriverAttendance = DriverAttendanceDto;

export interface UseDriverAttendanceResult {
  attendance: DriverAttendance | null;
  deliverySafety: DriverDeliverySafetyDto;
  /** 現在の勤務日の車両点検を実施済みか (#401 初回出発打刻の警告用)。応答欠落時は false。 */
  inspectionDone: boolean;
  loaded: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDriverAttendance(
  opts: DriverFetcher & { pollMs?: number },
): UseDriverAttendanceResult {
  const [attendance, setAttendance] = useState<DriverAttendance | null>(null);
  const [inspectionDoneToday, setInspectionDoneToday] = useState(false);
  const [deliverySafety, setDeliverySafety] = useState<DriverDeliverySafetyDto>({
    allowed: false,
    latest_result: null,
    reason: "no_attendance",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!opts.session) {
      setAttendance(null);
      setInspectionDoneToday(false);
      setDeliverySafety({ allowed: false, latest_result: null, reason: "no_attendance" });
      setLoaded(true);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fetchFn = authedFetch(opts);
      const res = await fetchFn(
        `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/attendance/today`,
        {},
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
      }
      const payload = (await res.json()) as {
        data: DriverAttendance | null;
        delivery_safety: DriverDeliverySafetyDto;
        inspection_done?: boolean;
      };
      setAttendance(payload.data);
      setInspectionDoneToday(payload.inspection_done ?? false);
      // delivery_safety が欠落した応答でも UI が undefined 参照でクラッシュしないよう防御する
      // (返却型 deliverySafety は非 null 契約。欠落は安全側の lookup_failed に倒す)。
      setDeliverySafety(
        payload.delivery_safety ?? { allowed: false, latest_result: null, reason: "lookup_failed" },
      );
    } catch (e) {
      setDeliverySafety({ allowed: false, latest_result: null, reason: "lookup_failed" });
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [opts.apiBaseUrl, opts.session, opts.fetchImpl]);

  useEffect(() => {
    void refetch();
    const pollMs = opts.pollMs ?? 0;
    const id = pollMs > 0 ? setInterval(() => void refetch(), pollMs) : undefined;
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refetch();
    };
    if (typeof document !== "undefined")
      document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (id) clearInterval(id);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [refetch, opts.pollMs]);

  return {
    attendance,
    deliverySafety,
    inspectionDone: inspectionDoneToday,
    loaded,
    loading,
    error,
    refetch,
  };
}

/**
 * 打刻入力. 型の単一源泉は @carry/contract (driver-attendance.ts)。
 * F16 安全/法令: alcohol_result は必須。fail でも出勤を記録し、配送実行だけを禁止する。
 */
export type ClockInInput = ClockInInputDto;

export type ClockOutInput = ClockOutInputDto;

export async function clockInDriver(
  opts: DriverFetcher,
  input: ClockInInput,
): Promise<DriverAttendance> {
  return driverPostJson(opts, "/api/v1/driver/attendance/clock-in", input);
}

export async function clockOutDriver(
  opts: DriverFetcher,
  input: ClockOutInput = {},
): Promise<DriverAttendance> {
  return driverPostJson(opts, "/api/v1/driver/attendance/clock-out", input);
}

/** F10 GPS 現在地報告の入力. 型の単一源泉は @carry/contract (driver-location.ts)。 */
export type ReportLocationInput = ReportLocationInputDto;

/** F10 リアルタイム: GPS 現在地を報告する (距離ベース間引きは呼出側 = location-reporter で行う)。 */
export async function reportLocationDriver(
  opts: DriverFetcher,
  input: ReportLocationInput,
): Promise<{ recorded_at: string }> {
  return driverPostJson(opts, "/api/v1/driver/locations", input);
}

// ───── 経費 ─────

/** 経費 DTO / 作成入力. 型の単一源泉は @carry/contract (driver-expense.ts)。 */
export type DriverExpense = DriverExpenseDto;

export type CreateExpenseInput = CreateExpenseInputDto;

export type UpdateExpenseInput = UpdateExpenseInputDto;

export async function createDriverExpense(
  opts: DriverFetcher,
  input: CreateExpenseInput,
): Promise<DriverExpense> {
  return driverPostJson(opts, "/api/v1/driver/expenses", input);
}

export async function updateDriverExpense(
  opts: DriverFetcher,
  expensePublicId: string,
  input: UpdateExpenseInput,
): Promise<DriverExpense> {
  return driverPatchJson(opts, `/api/v1/driver/expenses/${expensePublicId}`, input);
}

export async function deleteDriverExpense(
  opts: DriverFetcher,
  expensePublicId: string,
): Promise<{ success: true }> {
  return driverDelete(opts, `/api/v1/driver/expenses/${expensePublicId}`);
}

/**
 * 経費の領収書画像をアップロードする (multipart)。createDriverExpense で得た public_id に紐付ける。
 * JPEG/PNG/PDF・10MB まで。成功時は presigned URL を返す。
 */
export async function uploadDriverExpenseReceipt(
  opts: DriverFetcher,
  expensePublicId: string,
  file: File,
): Promise<{ public_id: string; receipt_image_url: string | null }> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const form = new FormData();
  form.append("file", file);
  const res = await fetchFn(
    `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/expenses/${expensePublicId}/receipt`,
    {
      method: "POST",
      body: form,
    },
  );
  if (!res.ok) {
    throw await toDriverHttpError(res);
  }
  const payload = (await res.json()) as {
    data: { public_id: string; receipt_image_url: string | null };
  };
  return payload.data;
}

export function useDriverExpenses(
  opts: DriverFetcher & { from?: string; to?: string; pollMs?: number },
): {
  expenses: DriverExpense[] | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [expenses, setExpenses] = useState<DriverExpense[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!opts.session) {
      setExpenses(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fetchFn = authedFetch(opts);
      const qs = new URLSearchParams();
      if (opts.from) qs.set("from", opts.from);
      if (opts.to) qs.set("to", opts.to);
      const url = `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/expenses${qs.toString() ? `?${qs}` : ""}`;
      const res = await fetchFn(url, {});
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
      }
      const payload = (await res.json()) as { data: DriverExpense[] };
      setExpenses(payload.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [opts.apiBaseUrl, opts.session, opts.fetchImpl, opts.from, opts.to]);

  useEffect(() => {
    void refetch();
    const pollMs = opts.pollMs ?? 0;
    if (pollMs <= 0) return;
    const id = setInterval(() => void refetch(), pollMs);
    return () => clearInterval(id);
  }, [refetch, opts.pollMs]);

  return { expenses, loading, error, refetch };
}

/** #616 経費の紐付け先候補 (対象日の配達先)。型の単一源泉は @carry/contract。 */
export type ExpenseStopOption = ExpenseStopOptionDto;

/**
 * #616 対象日の配達先一覧を取得する。ETC のように後日金額が判明する経費を
 * 過去日の配達先へ紐付けられるよう、日付を切り替えるたびに取り直す。
 */
export function useDriverExpenseStopOptions(
  opts: DriverFetcher & { date: string; enabled?: boolean },
): {
  options: ExpenseStopOption[];
  loading: boolean;
  error: string | null;
} {
  const [options, setOptions] = useState<ExpenseStopOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    // 配達先が固定されている画面 (配送先詳細からの登録) は候補を引く必要がない。
    if (opts.enabled === false || !opts.session || !opts.date) {
      setOptions([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fetchFn = authedFetch(opts);
      const url = `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/expense-stop-options?date=${opts.date}`;
      const res = await fetchFn(url, {});
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
      }
      const payload = (await res.json()) as { data?: ExpenseStopOption[] };
      // 紐付けは任意機能なので、想定外の応答でも経費入力そのものを止めない。
      setOptions(Array.isArray(payload.data) ? payload.data : []);
    } catch (e) {
      // 候補が取れなくても経費そのものは登録できる (紐付けは任意) ため、エラーは表示のみ。
      setOptions([]);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [opts.apiBaseUrl, opts.session, opts.fetchImpl, opts.date, opts.enabled]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { options, loading, error };
}

// ───── 日報 ─────

/** 日報 DTO / ステータス / 提出入力. 型の単一源泉は @carry/contract (driver-daily-report.ts)。 */
export type DailyReportStatus = DailyReportStatusDto;

export type DriverDailyReport = DriverDailyReportDto;

export type SubmitDailyReportInput = SubmitDailyReportInputDto;

export type DailyReportRecoveryCandidate = DailyReportRecoveryCandidateDto;

export type ListDailyReportRecoveryCandidatesInput = ListDailyReportRecoveryCandidatesInputDto;

export type RecoverDailyReportInput = RecoverDailyReportInputDto;

export type ResubmitDailyReportInput = ResubmitDailyReportInputDto;

export type DriverDailyReportAlreadyExistsData = DailyReportAlreadyExistsErrorDataDto;

export function isDriverDailyReportAlreadyExistsError(
  error: unknown,
): error is DriverHttpError<DriverDailyReportAlreadyExistsData> & {
  data: DriverDailyReportAlreadyExistsData;
} {
  if (!(error instanceof DriverHttpError) || error.status !== 409) return false;
  return DailyReportAlreadyExistsErrorData.safeParse(error.data).success;
}

/**
 * 本日の日報を明示的に取得する。経費・給油の変更後など、画面が未表示でも
 * 集計結果を再取得する必要がある経路から利用する。
 */
export async function getDriverTodayDailyReport(
  opts: DriverFetcher,
): Promise<DriverDailyReport | null> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const res = await fetchFn(
    `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/daily-report/today`,
    {},
  );
  if (!res.ok) throw await toDriverHttpError(res);
  const payload = (await res.json()) as { data: DriverDailyReport | null };
  return payload.data;
}

export function useDriverDailyReport(opts: DriverFetcher & { pollMs?: number }): {
  report: DriverDailyReport | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [report, setReport] = useState<DriverDailyReport | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!opts.session) {
      setReport(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setReport(await getDriverTodayDailyReport(opts));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [opts.apiBaseUrl, opts.session, opts.fetchImpl]);

  useEffect(() => {
    void refetch();
    const pollMs = opts.pollMs ?? 0;
    if (pollMs <= 0) return;
    const id = setInterval(() => void refetch(), pollMs);
    return () => clearInterval(id);
  }, [refetch, opts.pollMs]);

  return { report, loading, error, refetch };
}

export async function submitDriverDailyReport(
  opts: DriverFetcher,
  input: SubmitDailyReportInput,
): Promise<DriverDailyReport> {
  return driverPostJson(opts, "/api/v1/driver/daily-report/submit", input);
}

export function useDriverDailyReportRecoveryCandidates(
  opts: DriverFetcher & ListDailyReportRecoveryCandidatesInput,
): {
  candidates: DailyReportRecoveryCandidate[] | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [candidates, setCandidates] = useState<DailyReportRecoveryCandidate[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!opts.session) {
      setCandidates(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fetchFn = authedFetch(opts);
      const qs = new URLSearchParams();
      if (opts.month) qs.set("month", opts.month);
      const res = await fetchFn(
        `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/daily-reports/recovery-candidates${qs.toString() ? `?${qs}` : ""}`,
        {},
      );
      if (!res.ok) throw await toDriverHttpError(res);
      const payload = (await res.json()) as { data: DailyReportRecoveryCandidate[] };
      setCandidates(payload.data);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "復旧候補の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  }, [opts.apiBaseUrl, opts.session, opts.fetchImpl, opts.month]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { candidates, loading, error, refetch };
}

export async function recoverDriverDailyReport(
  opts: DriverFetcher,
  input: RecoverDailyReportInput,
): Promise<DriverDailyReport> {
  return driverPostJson(opts, "/api/v1/driver/daily-reports/recover", input);
}

export async function getDriverDailyReport(
  opts: DriverFetcher,
  publicId: string,
): Promise<DriverDailyReport> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const res = await fetchFn(
    `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/daily-reports/${encodeURIComponent(publicId)}`,
    {},
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `HTTP ${res.status}`);
  }
  const payload = (await res.json()) as { data: DriverDailyReport };
  return payload.data;
}

export async function resubmitDriverDailyReport(
  opts: DriverFetcher,
  publicId: string,
  input: ResubmitDailyReportInput,
): Promise<DriverDailyReport> {
  return driverPostJson(
    opts,
    `/api/v1/driver/daily-reports/${encodeURIComponent(publicId)}/resubmit`,
    input,
  );
}

// ───── 車両点検 (12 項目) ─────

/** 点検 DTO / 結果 enum / 作成入力. 型の単一源泉は @carry/contract (driver-inspection.ts)。 */
export type InspectionItemResult = InspectionItemResultDto;

export type VehicleInspectionDto = VehicleInspectionContractDto;

export type CreateVehicleInspectionInput = CreateVehicleInspectionInputDto;

export async function createVehicleInspection(
  opts: DriverFetcher,
  input: CreateVehicleInspectionInput,
): Promise<VehicleInspectionDto> {
  return driverPostJson(opts, "/api/v1/driver/vehicle-inspections", input);
}

// ───── アルコール検査 ─────

/** アルコール検査 DTO / 結果 enum / 作成入力. 型の単一源泉は @carry/contract (driver-alcohol-check.ts)。 */
export type AlcoholCheckResult = AlcoholCheckResultDto;

export type AlcoholCheckDto = AlcoholCheckContractDto;

export type CreateAlcoholCheckInput = CreateAlcoholCheckInputDto;

export async function createAlcoholCheck(
  opts: DriverFetcher,
  input: CreateAlcoholCheckInput,
): Promise<AlcoholCheckDto> {
  return driverPostJson(opts, "/api/v1/driver/alcohol-checks", input);
}

// ───── 給油記録 ─────

/** 給油 DTO / 作成入力. 型の単一源泉は @carry/contract (driver-refueling.ts)。 */
export type RefuelingDto = RefuelingContractDto;

export type CreateRefuelingInput = CreateRefuelingInputDto;

export type UpdateRefuelingInput = UpdateRefuelingInputDto;

export function useDriverRefuelings(opts: DriverFetcher & { pollMs?: number }): {
  refuelings: RefuelingDto[] | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [refuelings, setRefuelings] = useState<RefuelingDto[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!opts.session) {
      setRefuelings(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fetchFn = authedFetch(opts);
      const res = await fetchFn(
        `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/refuelings/today`,
        {},
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
      }
      const payload = (await res.json()) as { data: RefuelingDto[] };
      setRefuelings(payload.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [opts.apiBaseUrl, opts.session, opts.fetchImpl]);

  useEffect(() => {
    void refetch();
    const pollMs = opts.pollMs ?? 0;
    if (pollMs <= 0) return;
    const id = setInterval(() => void refetch(), pollMs);
    return () => clearInterval(id);
  }, [refetch, opts.pollMs]);

  return { refuelings, loading, error, refetch };
}

export async function createDriverRefueling(
  opts: DriverFetcher,
  input: CreateRefuelingInput,
): Promise<RefuelingDto> {
  return driverPostJson(opts, "/api/v1/driver/refuelings", input);
}

export async function updateDriverRefueling(
  opts: DriverFetcher,
  refuelingPublicId: string,
  input: UpdateRefuelingInput,
): Promise<RefuelingDto> {
  return driverPatchJson(opts, `/api/v1/driver/refuelings/${refuelingPublicId}`, input);
}

export async function deleteDriverRefueling(
  opts: DriverFetcher,
  refuelingPublicId: string,
): Promise<{ success: true }> {
  return driverDelete(opts, `/api/v1/driver/refuelings/${refuelingPublicId}`);
}

/**
 * 給油記録の領収書画像をアップロードする (multipart)。createDriverRefueling で得た public_id に紐付ける。
 * JPEG/PNG/PDF・10MB まで。成功時は presigned URL を返す。
 */
export async function uploadDriverRefuelingReceipt(
  opts: DriverFetcher,
  refuelingPublicId: string,
  file: File,
): Promise<{ public_id: string; receipt_image_url: string | null }> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const form = new FormData();
  form.append("file", file);
  const res = await fetchFn(
    `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/refuelings/${refuelingPublicId}/receipt`,
    {
      method: "POST",
      body: form,
    },
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `HTTP ${res.status}`);
  }
  const payload = (await res.json()) as {
    data: { public_id: string; receipt_image_url: string | null };
  };
  return payload.data;
}

// ───── 車両一覧 (inspection 用) ─────

export type DriverVehicleOption = Pick<
  VehicleDto,
  "id" | "vehicle_number" | "vehicle_name" | "user_driver_id"
>;

export function useDriverVehicles(opts: DriverFetcher): {
  vehicles: DriverVehicleOption[] | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [vehicles, setVehicles] = useState<DriverVehicleOption[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!opts.session) {
      setVehicles(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fetchFn = authedFetch(opts);
      const res = await fetchFn(`${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/vehicles`, {});
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
      }
      const payload = (await res.json()) as { data: DriverVehicleOption[] };
      setVehicles(payload.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [opts.apiBaseUrl, opts.session, opts.fetchImpl]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { vehicles, loading, error, refetch };
}

// ───── 通知設定 (F13) ─────

export function useNotificationSettings(opts: DriverFetcher): {
  settings: NotificationSettingsDto | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [settings, setSettings] = useState<NotificationSettingsDto | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!opts.session) {
      setSettings(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fetchFn = authedFetch(opts);
      const res = await fetchFn(
        `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/settings/notifications`,
        {},
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
      }
      const payload = (await res.json()) as { data: NotificationSettingsDto };
      setSettings(payload.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [opts.apiBaseUrl, opts.session, opts.fetchImpl]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { settings, loading, error, refetch };
}

export async function updateNotificationSettings(
  opts: DriverFetcher,
  input: UpdateNotificationSettingsInputDto,
): Promise<NotificationSettingsDto> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const res = await fetchFn(`${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/settings/notifications`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `HTTP ${res.status}`);
  }
  const payload = (await res.json()) as { data: NotificationSettingsDto };
  return payload.data;
}

async function driverPostJson<T>(opts: DriverFetcher, path: string, body: unknown): Promise<T> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const res = await fetchFn(`${opts.apiBaseUrl.replace(/\/$/, "")}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await toDriverHttpError(res);
  }
  const payload = (await res.json()) as { data: T };
  return payload.data;
}

async function driverPatchJson<T>(opts: DriverFetcher, path: string, body: unknown): Promise<T> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const res = await fetchFn(`${opts.apiBaseUrl.replace(/\/$/, "")}${path}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await toDriverHttpError(res);
  }
  const payload = (await res.json()) as { data: T };
  return payload.data;
}

async function driverDelete(opts: DriverFetcher, path: string): Promise<{ success: true }> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const res = await fetchFn(`${opts.apiBaseUrl.replace(/\/$/, "")}${path}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw await toDriverHttpError(res);
  }
  return (await res.json()) as { success: true };
}

// ───── 配送指示確認 (F04 API-38) ─────

/**
 * 配送指示確認 (F04). ドライバーが指示詳細を表示した時点で fire-and-forget で呼び、
 * instruction_reads へ冪等に記録する。失敗時はサイレント (業務継続性優先)。
 */
export async function confirmInstruction(
  opts: DriverFetcher,
  routePublicId: string,
): Promise<{ route_id: string; read_at: string } | null> {
  if (!opts.session) return null;
  const fetchFn = authedFetch(opts);
  try {
    const res = await fetchFn(
      `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/instructions/${routePublicId}/read`,
      {
        method: "POST",
      },
    );
    if (!res.ok) return null;
    const payload = (await res.json()) as { data: { route_id: string; read_at: string } };
    return payload.data;
  } catch {
    return null;
  }
}

// ───── 過去日報 (6.40) ─────

/**
 * driver 本人の過去日報一覧 (月 / ステータスフィルタ).
 * month 未指定時はサーバが当月を補完する.
 */
export function useDriverDailyReports(opts: DriverFetcher & { month?: string; status?: string }): {
  reports: DriverDailyReportDto[] | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [reports, setReports] = useState<DriverDailyReportDto[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!opts.session) {
      setReports(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fetchFn = authedFetch(opts);
      const qs = new URLSearchParams();
      if (opts.month) qs.set("month", opts.month);
      if (opts.status) qs.set("status", opts.status);
      const url = `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/daily-reports${qs.toString() ? `?${qs}` : ""}`;
      const res = await fetchFn(url, {});
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
      }
      const payload = (await res.json()) as { data: DriverDailyReportDto[] };
      setReports(payload.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [opts.apiBaseUrl, opts.session, opts.fetchImpl, opts.month, opts.status]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { reports, loading, error, refetch };
}

// ───── 注文書プレビュー (6.37a) ─────

/**
 * stop に紐付く注文書の有無 (has_document) を meta API で確認する.
 * 注文書ボタンの活性判定を注文番号有無ではなく実体有無に一致させるために使う (ux-orderdoc-button-gating-mismatch).
 * stopPublicId が null の間は問い合わせない. 判定が確定するまで hasDocument は null.
 */
export function useDriverOrderDocumentMeta(opts: DriverFetcher & { stopPublicId: string | null }): {
  hasDocument: boolean | null;
  loading: boolean;
} {
  const [hasDocument, setHasDocument] = useState<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!opts.session || !opts.stopPublicId) {
      setHasDocument(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const fetchFn = authedFetch(opts);
    fetchFn(
      `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/stops/${opts.stopPublicId}/order-document/meta`,
      {},
    )
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const payload = (await res.json()) as { data?: { has_document?: boolean } };
        if (!cancelled) setHasDocument(payload.data?.has_document ?? false);
      })
      .catch(() => {
        // 取得失敗時は判定不能 (null)。ボタンは注文番号有無のフォールバックに委ねる。
        if (!cancelled) setHasDocument(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [opts.apiBaseUrl, opts.session, opts.fetchImpl, opts.stopPublicId]);

  return { hasDocument, loading };
}

/**
 * stop に紐付く注文書を httpOnly セッション Cookie で取得し blob URL 化する.
 * stopPublicId が null の間は取得しない. アンマウント時に ObjectURL を解放する.
 */
export function useDriverOrderDocument(opts: DriverFetcher & { stopPublicId: string | null }): {
  blobUrl: string | null;
  contentType: string | null;
  loading: boolean;
  error: string | null;
} {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opts.session || !opts.stopPublicId) {
      setBlobUrl(null);
      return;
    }
    let createdUrl: string | null = null;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const fetchFn = authedFetch(opts);
    fetchFn(
      `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/stops/${opts.stopPublicId}/order-document`,
      {},
    )
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ct = res.headers.get("Content-Type");
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        setContentType(ct);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [opts.apiBaseUrl, opts.session, opts.fetchImpl, opts.stopPublicId]);

  return { blobUrl, contentType, loading, error };
}

// ───── F14: 配送実行ファミリー (Slice 1-4) ─────

/** F14 Slice 3: ステータス巻き戻し (completed → in_transit). */
export async function revertDriverStopStatus(
  opts: DriverFetcher,
  stopPublicId: string,
  reason?: string,
): Promise<{
  id: string;
  status: string;
  previous_status: string;
  reverted_at: string;
  audit_id: string;
}> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const res = await fetchFn(
    `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/stops/${stopPublicId}/status/revert`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reason !== undefined ? { reason } : {}),
    },
  );
  if (!res.ok) {
    throw await toDriverHttpError(res);
  }
  const payload = (await res.json()) as {
    data: {
      id: string;
      status: string;
      previous_status: string;
      reverted_at: string;
      audit_id: string;
    };
  };
  return payload.data;
}

export interface EditDriverStopTimestampsInput {
  pickup_completed_at?: string;
  completed_at?: string;
  reason: string;
}

/** F14 Slice 3: 打刻修正 (当日内、reason 必須). */
export async function editDriverStopTimestamps(
  opts: DriverFetcher,
  stopPublicId: string,
  input: EditDriverStopTimestampsInput,
): Promise<{
  id: string;
  pickup_completed_at: string | null;
  completed_at: string | null;
  audit_ids: string[];
}> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const res = await fetchFn(
    `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/stops/${stopPublicId}/status-timestamps`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) {
    throw await toDriverHttpError(res);
  }
  const payload = (await res.json()) as {
    data: {
      id: string;
      pickup_completed_at: string | null;
      completed_at: string | null;
      audit_ids: string[];
    };
  };
  return payload.data;
}

/**
 * #769: 過去日の配送実績の後追い登録。
 *
 * 当日の打刻とは別経路で、送るのは「実際の時刻」と理由。保存時点で実績へ反映し、確認は運転日報の承認で行う。
 * 可否判定 (期間・ロック) はサーバが返す eligibility をそのまま使い、画面側に判定を持たせない。
 */
export interface DriverBackfillEligibility {
  stop_id: string;
  work_date: string;
  driver_public_id: string | null;
  allowed: boolean;
  blocked_reason: BackfillBlockedReasonDto | null;
  latest_request: {
    id: string;
    status: BackfillRequestStatusDto;
    review_note: string | null;
    reviewed_at: string | null;
  } | null;
}

export type DriverBackfillRequest = BackfillRequestDto;

export interface CreateDriverBackfillRequestInput {
  actual_arrival: string;
  actual_departure: string;
  arrival_odometer?: number | null;
  reason: string;
}

/** 対象 stop に後追い登録できるかをサーバへ問い合わせる。 */
export async function getDriverBackfillEligibility(
  opts: DriverFetcher,
  stopPublicId: string,
): Promise<DriverBackfillEligibility> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const res = await fetchFn(
    `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/stops/${stopPublicId}/backfill-eligibility`,
  );
  if (!res.ok) throw await toDriverHttpError(res);
  const payload = (await res.json()) as { data: DriverBackfillEligibility };
  return payload.data;
}

/** 後追い登録を保存する。保存時点で実績へ反映される。承認は運転日報で行う。 */
export async function createDriverBackfillRequest(
  opts: DriverFetcher,
  stopPublicId: string,
  input: CreateDriverBackfillRequestInput,
): Promise<DriverBackfillRequest> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const res = await fetchFn(
    `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/stops/${stopPublicId}/backfill-requests`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) throw await toDriverHttpError(res);
  const payload = (await res.json()) as { data: DriverBackfillRequest };
  return payload.data;
}

/** 承認待ちの申請を取り下げる (本人のみ)。 */
export async function cancelDriverBackfillRequest(
  opts: DriverFetcher,
  requestPublicId: string,
): Promise<DriverBackfillRequest> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const res = await fetchFn(
    `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/backfill-requests/${requestPublicId}/cancel`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" },
  );
  if (!res.ok) throw await toDriverHttpError(res);
  const payload = (await res.json()) as { data: DriverBackfillRequest };
  return payload.data;
}

export interface CreateDriverDelayReportInput {
  delay_reason: "traffic" | "production_delay" | "previous_case_delay" | "other";
  eta_type: "unknown" | "specified";
  eta_at?: string;
  impact_note?: string;
}

/** F14 Slice 2: 遅延報告. 成功時に admin/staff へ LINE Push が発火する. */
export async function createDriverDelayReport(
  opts: DriverFetcher,
  stopPublicId: string,
  input: CreateDriverDelayReportInput & { request_id: string },
): Promise<{
  id: string;
  stop_id: string;
  delay_reason: string;
  eta_type: string;
  eta_at: string | null;
  impact_note: string | null;
  reported_at: string;
}> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const res = await fetchFn(
    `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/stops/${stopPublicId}/delay-reports`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) {
    throw await toDriverHttpError(res);
  }
  const payload = (await res.json()) as { data: never };
  return payload.data;
}

/** F14 Slice 4: 配達証跡画像 multipart upload. JPEG/PNG、10MB 以内. */
export async function uploadDriverProof(
  opts: DriverFetcher,
  stopPublicId: string,
  file: File,
  imageType: "signature" | "photo",
): Promise<{ id: string; image_type: string; image_url: string; created_at: string }> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const fd = new FormData();
  fd.append("file", file);
  fd.append("image_type", imageType);
  const res = await fetchFn(
    `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/stops/${stopPublicId}/proof`,
    {
      method: "POST",
      body: fd,
    },
  );
  if (!res.ok) {
    throw await toDriverHttpError(res);
  }
  const payload = (await res.json()) as {
    data: { id: string; image_type: string; image_url: string; created_at: string };
  };
  return payload.data;
}

/** F14 Slice 4: 配達証跡画像一覧 (presigned URL 込). */
export async function listDriverStopProof(
  opts: DriverFetcher,
  stopPublicId: string,
): Promise<{ id: string; image_type: string; image_url: string; created_at: string }[]> {
  if (!opts.session) throw new Error("NO_SESSION");
  const fetchFn = authedFetch(opts);
  const res = await fetchFn(
    `${opts.apiBaseUrl.replace(/\/$/, "")}/api/v1/driver/stops/${stopPublicId}/proof`,
    {
      method: "GET",
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
  }
  const payload = (await res.json()) as {
    data: { id: string; image_type: string; image_url: string; created_at: string }[];
  };
  return payload.data;
}
